import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/formatAgent'

export function ActionBadge({ action, status }) {
  const value = (action || status || '').toLowerCase()
  if (!value) return <span className="text-muted-foreground">—</span>
  if (value.includes('block') || value.includes('quarantine') || value.includes('delete')) {
    return <Badge variant="destructive">{action || status}</Badge>
  }
  if (value.includes('allow') || value.includes('ignore')) {
    return <Badge variant="secondary">{action || status}</Badge>
  }
  return <Badge variant="warning">{action || status}</Badge>
}

export function truncatePath(value, max = 72) {
  if (!value) return '—'
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

export function buildDetectionColumns({ showEndpoint = false, showCustomer = false } = {}) {
  const columns = []

  if (showEndpoint) {
    columns.push({
      id: 'hostname',
      label: 'Endpoint',
      sortValue: (row) => (row.endpoint_hostname ?? row.machine_name ?? '').toLowerCase(),
      render: (row) =>
        row.endpoint_id ? (
          <Link
            to={`/threatdown/endpoints/${row.endpoint_id}`}
            className="font-medium text-primary hover:underline"
          >
            {row.endpoint_hostname || row.machine_name || '—'}
          </Link>
        ) : (
          row.endpoint_hostname || row.machine_name || '—'
        ),
    })
  }

  if (showCustomer) {
    columns.push({
      id: 'customer',
      label: 'Customer',
      sortValue: (row) => (row.customer_name ?? '').toLowerCase(),
      render: (row) => row.customer_name || '—',
      className: 'whitespace-nowrap',
    })
  }

  columns.push(
    {
      id: 'scanned_at',
      label: 'Detected',
      sortValue: (row) => row.scanned_at ?? '',
      render: (row) => formatDateTime(row.scanned_at),
      className: 'whitespace-nowrap',
    },
    {
      id: 'threat_name',
      label: 'Threat',
      sortValue: (row) => (row.threat_name ?? '').toLowerCase(),
      render: (row) => row.threat_name || '—',
    },
    {
      id: 'category',
      label: 'Category',
      sortValue: (row) => (row.category ?? '').toLowerCase(),
      render: (row) => row.category || '—',
      className: 'whitespace-nowrap',
    },
    {
      id: 'action_taken',
      label: 'Action',
      sortValue: (row) => (row.action_taken ?? row.status ?? '').toLowerCase(),
      render: (row) => <ActionBadge action={row.action_taken} status={row.status} />,
      className: 'whitespace-nowrap',
    },
  )

  if (showEndpoint) {
    columns.push({
      id: 'account_name',
      label: 'Site',
      sortValue: (row) => (row.account_name ?? '').toLowerCase(),
      render: (row) => row.account_name || '—',
      className: 'whitespace-nowrap',
    })
  }

  columns.push(
    {
      id: 'path',
      label: 'Path / target',
      sortValue: (row) => (row.path ?? '').toLowerCase(),
      render: (row) => (
        <span className="font-mono text-xs" title={row.path || undefined}>
          {truncatePath(row.path)}
        </span>
      ),
    },
    {
      id: 'process_name',
      label: 'Process',
      sortValue: (row) => (row.process_name ?? '').toLowerCase(),
      render: (row) => (
        <span className="font-mono text-xs" title={row.process_name || undefined}>
          {truncatePath(row.process_name, 56)}
        </span>
      ),
    },
  )

  return columns
}
